import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";

export type AppView =
  | "dashboard"
  | "codes"
  | "create"
  | "edit"
  | "details"
  | "analytics"
  | "health"
  | "notifications"
  | "campaigns"
  | "landing"
  | "protection"
  | "workspace"
  | "domains"
  | "settings";

interface AppShellProps {
  currentView: AppView;
  onViewChange: (view: AppView) => void;
  onCreate: () => void;
  qrCount: number;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  children: React.ReactNode;
}

interface IconProps {
  name:
    | "overview"
    | "codes"
    | "plus"
    | "settings"
    | "search"
    | "menu"
    | "logout"
    | "chevron"
    | "sparkles"
    | "analytics"
    | "health"
    | "bell"
    | "campaign"
    | "landing"
    | "shield"
    | "folder"
    | "domain";
  className?: string;
}

const Icon: React.FC<IconProps> = ({ name, className = "h-5 w-5" }) => {
  const paths = {
    overview: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="2" />
        <rect x="14" y="3" width="7" height="7" rx="2" />
        <rect x="3" y="14" width="7" height="7" rx="2" />
        <rect x="14" y="14" width="7" height="7" rx="2" />
      </>
    ),
    codes: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <path d="M14 14h3v3h-3zM19 14h2v2M19 19h2v2M14 19h3v2" />
      </>
    ),
    plus: (
      <>
        <path d="M12 5v14M5 12h14" />
      </>
    ),
    settings: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.03 1.55V21h-4v-.08A1.7 1.7 0 0 0 9 19.37a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.63 15 1.7 1.7 0 0 0 3.08 14H3v-4h.08A1.7 1.7 0 0 0 4.63 9a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.63a1.7 1.7 0 0 0 1-1.55V3h4v.08A1.7 1.7 0 0 0 15 4.63a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.37 9a1.7 1.7 0 0 0 1.55 1H21v4h-.08A1.7 1.7 0 0 0 19.4 15z" />
      </>
    ),
    search: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-4-4" />
      </>
    ),
    menu: (
      <>
        <path d="M4 7h16M4 12h16M4 17h16" />
      </>
    ),
    logout: (
      <>
        <path d="M10 17l5-5-5-5M15 12H3" />
        <path d="M14 3h4a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3h-4" />
      </>
    ),
    chevron: <path d="m9 18 6-6-6-6" />,
    sparkles: (
      <>
        <path d="m12 3-1.2 3.2L7.5 7.5l3.3 1.3L12 12l1.2-3.2 3.3-1.3-3.3-1.3L12 3zM5 14l-.8 2.2L2 17l2.2.8L5 20l.8-2.2L8 17l-2.2-.8L5 14zM18 13l-.9 2.1L15 16l2.1.9L18 19l.9-2.1L21 16l-2.1-.9L18 13z" />
      </>
    ),
    analytics: (
      <>
        <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
        <path d="m3 7 6-4 6 6 6-5" />
      </>
    ),
    health: (
      <>
        <path d="M3 12h4l2-5 4 10 2-5h6" />
        <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.7-8.6a5.5 5.5 0 0 0 .1-7.8z" />
      </>
    ),
    bell: (
      <>
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
        <path d="M10 21h4" />
      </>
    ),
    campaign: (
      <>
        <path d="m3 11 15-6v14L3 13z" />
        <path d="M11.6 15.8 13 21H8l-1.4-7" />
      </>
    ),
    landing: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="3" />
        <path d="M3 8h18M8 21V8" />
      </>
    ),
    shield: (
      <>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </>
    ),
    folder: <path d="M3 5h6l2 2h10v12H3z" />,
    domain: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
      </>
    ),
  };

  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
};

const AppShell: React.FC<AppShellProps> = ({
  currentView,
  onViewChange,
  onCreate,
  qrCount,
  searchQuery,
  onSearchChange,
  children,
}) => {
  const { currentUser, logout } = useAuth();
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const stored = Number(localStorage.getItem("redirectqr-sidebar-width"));
    return stored >= 228 && stored <= 320 ? stored : 264;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("redirectqr-sidebar-collapsed") === "true",
  );

  useEffect(() => {
    if (!isResizing) return;

    const handleMove = (event: MouseEvent) => {
      if (sidebarCollapsed) setSidebarCollapsed(false);
      setSidebarWidth(Math.min(320, Math.max(228, event.clientX)));
    };
    const handleUp = () => setIsResizing(false);

    document.body.classList.add("is-resizing-sidebar");
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);

    return () => {
      document.body.classList.remove("is-resizing-sidebar");
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [isResizing, sidebarCollapsed]);

  useEffect(() => {
    localStorage.setItem("redirectqr-sidebar-width", String(sidebarWidth));
  }, [sidebarWidth]);

  useEffect(() => {
    localStorage.setItem(
      "redirectqr-sidebar-collapsed",
      String(sidebarCollapsed),
    );
  }, [sidebarCollapsed]);

  useEffect(() => setMobileOpen(false), [currentView]);

  const pageMeta = useMemo(() => {
    const pages: Record<
      AppView,
      { eyebrow: string; title: string; description: string }
    > = {
      dashboard: {
        eyebrow: "Workspace",
        title: "Übersicht",
        description: "Performance und Aktivität auf einen Blick.",
      },
      codes: {
        eyebrow: "Bibliothek",
        title: "QR-Codes",
        description: "Weiterleitungen finden, bearbeiten und organisieren.",
      },
      create: {
        eyebrow: "Builder",
        title: "QR-Code erstellen",
        description: "Ziel, Design und Export konfigurieren.",
      },
      edit: {
        eyebrow: "Builder",
        title: "QR-Code bearbeiten",
        description: "Weiterleitung oder Design aktualisieren.",
      },
      details: {
        eyebrow: "Insights",
        title: "QR-Details",
        description: "Analytics, Health, Versionen und Leads.",
      },
      analytics: {
        eyebrow: "Insights",
        title: "Analytics & Berichte",
        description: "Scans, Trends und Exporte.",
      },
      health: {
        eyebrow: "Insights",
        title: "Health-Monitor",
        description: "Erreichbarkeit, TLS und Redirect-Status.",
      },
      notifications: {
        eyebrow: "Insights",
        title: "Warnungen & Leads",
        description: "Health-Probleme und neue Kontakte.",
      },
      campaigns: {
        eyebrow: "Werkzeuge",
        title: "Kampagnen & UTM",
        description: "Tracking-Parameter und geplante Ziele.",
      },
      landing: {
        eyebrow: "Werkzeuge",
        title: "Landingpages",
        description: "Mobile Seiten und Lead-Erfassung.",
      },
      protection: {
        eyebrow: "Werkzeuge",
        title: "Geschützte QR-Codes",
        description: "Passwörter, Limits und Zugangslinks.",
      },
      workspace: {
        eyebrow: "Workspace",
        title: "Ordner & Designs",
        description: "Codes organisieren und Designs wiederverwenden.",
      },
      domains: {
        eyebrow: "Workspace",
        title: "Eigene Domains",
        description: "Gebrandete Redirect-Domains und DNS.",
      },
      settings: {
        eyebrow: "Workspace",
        title: "Einstellungen",
        description: "Konto und Redirect-Konfiguration.",
      },
    };
    return pages[currentView];
  }, [currentView]);

  const navigate = (view: AppView) => {
    onViewChange(view);
    setMobileOpen(false);
  };

  const renderNavigation = (compact = false) => (
    <>
      <div className="sidebar-nav-section px-3">
        {!compact && (
          <p className="px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Übersicht
          </p>
        )}
        <div className="mt-1.5 space-y-0.5">
          <SidebarButton
            compact={compact}
            active={currentView === "dashboard"}
            icon="overview"
            label="Übersicht"
            onClick={() => navigate("dashboard")}
          />
        </div>
      </div>

      <div className="sidebar-nav-section px-3">
        {!compact && (
          <p className="px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            QR-Codes
          </p>
        )}
        <div className="mt-1.5 space-y-0.5">
          <SidebarButton
            compact={compact}
            active={currentView === "codes"}
            icon="codes"
            label="QR-Bibliothek"
            badge={qrCount}
            onClick={() => navigate("codes")}
          />
          <SidebarButton
            compact={compact}
            active={currentView === "create" || currentView === "edit"}
            icon="plus"
            label="QR erstellen"
            onClick={() => navigate("create")}
          />
        </div>
      </div>

      <div className="sidebar-nav-section px-3">
        {!compact && (
          <p className="px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Insights
          </p>
        )}
        <div className="mt-1.5 space-y-0.5">
          <SidebarButton
            compact={compact}
            active={currentView === "analytics"}
            icon="analytics"
            label="Analytics & Berichte"
            onClick={() => navigate("analytics")}
          />
          <SidebarButton
            compact={compact}
            active={currentView === "health"}
            icon="health"
            label="Health-Monitor"
            onClick={() => navigate("health")}
          />
          <SidebarButton
            compact={compact}
            active={currentView === "notifications"}
            icon="bell"
            label="Warnungen & Leads"
            onClick={() => navigate("notifications")}
          />
        </div>
      </div>

      <div className="sidebar-nav-section px-3">
        {!compact && (
          <p className="px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Werkzeuge
          </p>
        )}
        <div className="mt-1.5 space-y-0.5">
          <SidebarButton
            compact={compact}
            active={currentView === "campaigns"}
            icon="campaign"
            label="Kampagnen & UTM"
            onClick={() => navigate("campaigns")}
          />
          <SidebarButton
            compact={compact}
            active={currentView === "protection"}
            icon="shield"
            label="Geschützte Codes"
            onClick={() => navigate("protection")}
          />
        </div>
      </div>

      <div className="sidebar-nav-section px-3 pb-3">
        {!compact && (
          <p className="px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Workspace
          </p>
        )}
        <div className="mt-1.5 space-y-0.5">
          <SidebarButton
            compact={compact}
            active={currentView === "workspace"}
            icon="folder"
            label="Ordner & Designs"
            onClick={() => navigate("workspace")}
          />
          <SidebarButton
            compact={compact}
            active={currentView === "settings"}
            icon="settings"
            label="Einstellungen"
            onClick={() => navigate("settings")}
          />
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#f5f6f8] text-slate-950 lg:flex">
      <aside
        className="sticky top-0 hidden h-screen max-h-screen flex-shrink-0 flex-col overflow-visible border-r border-slate-800 bg-[#11151d] text-white lg:flex"
        style={{ width: sidebarCollapsed ? 72 : sidebarWidth }}
      >
        <SidebarHeader compact={sidebarCollapsed} />
        <nav className="sidebar-scroll-area min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain">
          {renderNavigation(sidebarCollapsed)}
        </nav>
        <SidebarFooter
          compact={sidebarCollapsed}
          email={currentUser?.email || ""}
          name={currentUser?.displayName || "Workspace admin"}
          onLogout={logout}
          onExpand={() => setSidebarCollapsed(false)}
        />
        <button
          type="button"
          onClick={() => setSidebarCollapsed((current) => !current)}
          className="sidebar-collapse-button"
          aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <Icon
            name="chevron"
            className={`h-4 w-4 transition-transform ${sidebarCollapsed ? "" : "rotate-180"}`}
          />
        </button>
        <button
          type="button"
          aria-label="Resize sidebar"
          title="Drag to resize"
          className={`sidebar-resizer ${isResizing ? "is-active" : ""}`}
          onMouseDown={() => setIsResizing(true)}
          onDoubleClick={() => {
            setSidebarCollapsed(false);
            setSidebarWidth(264);
          }}
        >
          <span />
        </button>
      </aside>

      {mobileOpen && (
        <button
          className="fixed inset-0 z-40 bg-slate-950/45 backdrop-blur-sm lg:hidden"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-[286px] flex-col bg-[#11151d] text-white shadow-2xl transition-transform duration-300 lg:hidden ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <SidebarHeader onClose={() => setMobileOpen(false)} />
        <nav className="sidebar-scroll-area min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain">
          {renderNavigation(false)}
        </nav>
        <SidebarFooter
          email={currentUser?.email || ""}
          name={currentUser?.displayName || "Workspace admin"}
          onLogout={logout}
        />
      </aside>

      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
          <div className="flex min-h-[72px] items-center gap-3 px-4 sm:px-6 xl:px-8">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="app-icon-button lg:hidden"
              aria-label="Open navigation"
            >
              <Icon name="menu" />
            </button>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                <span>{pageMeta.eyebrow}</span>
                <Icon name="chevron" className="h-3 w-3" />
                <span className="truncate text-slate-800">
                  {pageMeta.title}
                </span>
              </div>
              <h1 className="mt-0.5 truncate text-lg font-semibold tracking-tight text-slate-950 sm:text-xl">
                {pageMeta.title}
              </h1>
            </div>

            {(currentView === "dashboard" || currentView === "codes") && (
              <label className="relative hidden w-full max-w-[320px] md:block">
                <span className="sr-only">Search QR codes</span>
                <Icon
                  name="search"
                  className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => onSearchChange(event.target.value)}
                  placeholder="QR-Codes durchsuchen …"
                  className="app-search-input"
                />
              </label>
            )}

            {!["create", "edit", "campaigns", "landing", "protection"].includes(
              currentView,
            ) && (
              <button
                type="button"
                onClick={onCreate}
                className="app-primary-button"
              >
                <Icon name="plus" className="h-4 w-4" />
                <span className="hidden sm:inline">QR erstellen</span>
              </button>
            )}
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6 sm:py-8 xl:px-8">
          <div className="mb-6 md:hidden">
            {(currentView === "dashboard" || currentView === "codes") && (
              <label className="relative block">
                <span className="sr-only">Search QR codes</span>
                <Icon
                  name="search"
                  className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => onSearchChange(event.target.value)}
                  placeholder="QR-Codes durchsuchen …"
                  className="app-search-input"
                />
              </label>
            )}
          </div>
          {children}
        </main>
      </div>
    </div>
  );
};

interface SidebarButtonProps {
  active: boolean;
  icon: IconProps["name"];
  label: string;
  badge?: number;
  onClick: () => void;
  compact?: boolean;
}

const SidebarButton: React.FC<SidebarButtonProps> = ({
  active,
  icon,
  label,
  badge,
  onClick,
  compact = false,
}) => (
  <button
    type="button"
    onClick={onClick}
    title={compact ? label : undefined}
    aria-label={label}
    className={`sidebar-nav-item ${compact ? "justify-center px-0" : ""} ${active ? "is-active" : ""}`}
  >
    <Icon name={icon} />
    {!compact && (
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
    )}
    {!compact && typeof badge === "number" && (
      <span className="rounded-md bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-slate-300">
        {badge}
      </span>
    )}
  </button>
);

const SidebarHeader: React.FC<{ onClose?: () => void; compact?: boolean }> = ({
  onClose,
  compact = false,
}) => (
  <div
    className={`flex h-[72px] items-center gap-3 border-b border-white/10 ${compact ? "justify-center px-3" : "px-5"}`}
  >
    <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-xl bg-white text-slate-950 shadow-lg shadow-black/20">
      <svg
        viewBox="0 0 24 24"
        className="h-5 w-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM15 14h2v2h-2zM19 14h1v3h-3v3h-3v-2" />
      </svg>
    </div>
    {!compact && (
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold tracking-tight">
          RedirectQR<span className="text-blue-400">.de</span>
        </p>
        <p className="mt-0.5 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
          Control center
        </p>
      </div>
    )}
    {onClose && (
      <button
        type="button"
        className="rounded-lg p-2 text-slate-400 hover:bg-white/10 hover:text-white"
        onClick={onClose}
        aria-label="Close navigation"
      >
        ×
      </button>
    )}
  </div>
);

const SidebarFooter: React.FC<{
  email: string;
  name: string;
  onLogout: () => Promise<void>;
  compact?: boolean;
  onExpand?: () => void;
}> = ({ email, name, onLogout, compact = false, onExpand }) => (
  <div className="flex-shrink-0 border-t border-white/10 bg-[#11151d] p-3 shadow-[0_-12px_28px_rgba(17,21,29,.92)]">
    <div
      className={`flex items-center rounded-xl border border-white/[0.06] bg-white/[0.04] ${compact ? "justify-center p-2" : "gap-3 p-3"}`}
    >
      {compact ? (
        <button
          type="button"
          onClick={onExpand}
          className="grid h-9 w-9 place-items-center rounded-lg bg-blue-500/15 text-sm font-bold text-blue-300 transition hover:bg-blue-500/25 hover:text-white"
          aria-label="Open profile"
          title="Open profile"
        >
          {(name || email || "U").charAt(0).toUpperCase()}
        </button>
      ) : (
        <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg bg-blue-500/15 text-sm font-bold text-blue-300">
          {(name || email || "U").charAt(0).toUpperCase()}
        </div>
      )}
      {!compact && (
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-white">{name}</p>
          <p className="truncate text-xs text-slate-500">{email}</p>
        </div>
      )}
      <button
        type="button"
        onClick={() => void onLogout()}
        className={`rounded-lg p-2 text-slate-500 hover:bg-white/10 hover:text-white ${compact ? "hidden" : ""}`}
        aria-label="Sign out"
        title="Sign out"
      >
        <Icon name="logout" className="h-4 w-4" />
      </button>
    </div>
  </div>
);

export default AppShell;
